/**
 * Day and night palettes.
 *
 * Night is a real theme for the whole page, not a band across the header. Monochrome and
 * restrained per the A Dark Room direction: no hue beyond a trace of warmth in the greys, and
 * nothing is styled decoratively.
 *
 * Read these from styled-components as `${t('fg')}`, which falls back to the day palette when
 * there is no ThemeProvider above (Storybook renders components standalone).
 *
 * ── The passage between them ──────────────────────────────────────────────────────────────
 *
 * Turning the whole page over between near-white and near-black is a large thing to do to
 * someone's eyes, and in a town it happens on a click: beg, work, rob and sleep each call
 * calendar.nextStep, so the world flips under the player several times a minute. Three rules
 * keep that from reading as a strobe.
 *
 * 1. Everything moves together. A surface that snaps while the page fades is worse than no
 *    fade at all — the eye reads the snap as a flash against the drift. Every themed surface
 *    transitions on FADE below, so there is one clock for the whole page. Three things used
 *    to escape it: the notification log, painted with a background-image gradient that CSS
 *    cannot interpolate; the bindle's table label, which had no transition and hung on the
 *    page as a black bar for a full second; and the page background itself, which went
 *    briefly transparent at every flip — see FADE for that one, it is the subtle one.
 *
 * 2. The curve is near-linear. `ease` puts more than half the luminance change in the first
 *    quarter of the duration, which is the lurch. Interpolating sRGB across a white→black
 *    ramp is already close to perceptually even, so the honest curve is a straight line with
 *    only its ends softened.
 *
 * 3. The palette carries its own arrival time. The theme flips at the *start* of the
 *    transition, so the incoming palette's `fade` is the one that runs — which means day and
 *    night can cost different amounts with no state machine at all. Dawn is the longer of the
 *    two: dark-adapted eyes meeting the light is the direction that hurts.
 *
 * Slowing it also fixes the rapid-toggle case for free. A player clicking beg then sleep
 * inside a second now never reaches either endpoint — the transition retargets from
 * wherever it had got to, and the page shimmers around the greys instead of strobing between
 * white and black.
 */

// ── The two knobs. Turn these if the crossover feels wrong. ────────────────────────────────
//
// They were briefly 2400/3200, which was a mistake: the crossover read as jarring because
// surfaces were snapping out of step with each other, not because it was quick, and slowing
// it down only made the game feel unresponsive. With everything moving together and a
// near-linear curve, short is fine.
//
// Dusk, and the base for anything that just needs "the theme fade".
const FADE_MS = 900;
// Dawn. Longer: arriving in the light is the harder direction on the eye.
const DAWN_MS = 1200;
// The town's arrival animation, which is a separate idea from the day/night passage and must
// not inherit its length — the name of a town should land, not crawl in.
const RISE_MS = 1200;

const EASE = 'cubic-bezier(0.45, 0.05, 0.55, 0.95)';

/**
 * The shared transition timing, for `transition: color ${FADE}, background-color ${FADE}`.
 *
 * A function of props, so styled-components resolves it to a literal duration at class time.
 * It must NOT be a `var()`, and neither may any value being transitioned — this cost a whole
 * round to find. When the theme flips, the custom properties on :root and the declarations
 * that read them update in the same style recalculation, and for one frame a `var()` in a
 * transitioned declaration fails to resolve. A declaration that is invalid at computed-value
 * time falls back to the property's *initial* value, so `background-color: var(--bg)` became
 * `transparent` for a frame — and a transparent body reveals the browser canvas, which is
 * white. The page snapped to hard white and then spent the whole fade ramping alpha from zero
 * toward a colour that was already white, so the ramp was invisible.
 *
 * It looked like a dawn-only bug. It happened at dusk too: transparent-white against a page
 * that was already white is simply invisible, and the dark then faded in over it.
 *
 * Custom properties are still published for the plain CSS in index.css to read, but nothing
 * that transitions may go through one. Interpolate the palette directly.
 */
const FADE = props => `${t('fade')(props)}ms ${EASE}`;

const day = {
  isNight: false,
  fade: DAWN_MS,

  // Not pure white. #fff is a glare peak on a bright display, and it made the map — which
  // keeps its own paper white on purpose — invisible as an object sitting on the page.
  bg: '#f7f7f5',
  fg: '#111111',
  muted: '#666666',
  rule: '#c8c8c8',
  ruleStrong: '#000000',

  // Raised surfaces: event modal, tooltips, the debug panel. Now that the page is off pure
  // white, this reads as a lit surface above it rather than being identical to it.
  panel: '#ffffff',
  panelBorder: '#000000',
  panelShadow: 'rgba(0, 0, 0, 0.4)',
  tableBorder: '#808080',
  // The wash behind an open event.
  scrim: 'rgba(247, 247, 245, 0.6)',

  // The notification log fades out as entries age.
  logFrom: '#000000',
  logTo: '#eeeeee',
  logHover: '#000000',

  // Action buttons ramp from level 0 to max level.
  buttonFrom: [247, 247, 245],
  buttonTo: [0, 0, 0],
  buttonDisabled: '#b2b2b2',
  buttonDisabledBg: '#f7f7f5',
  onLight: '#000000',
  onDark: '#c5c5c5',
  cooldown: 221,

  // How deep the dark closes in from the edges when the traveler is dying. Not a colour: the
  // vignette is black in both palettes, because the dark coming up is the same dark at noon as at
  // midnight, and a gradient cannot be interpolated across a theme change anyway. This is the
  // opacity of the whole layer, which can. Day is the shallower of the two — a black edge against
  // a near-white page is already loud.
  vignette: 0.62,

  // Set from App.js off the media query, so anything with its own duration can honour it without
  // a @media block trying to out-specify generated rules. See useReducedMotion.
  reducedMotion: false,

  // The map is a physical object, so it keeps its paper white and is dimmed instead.
  mapFilter: 'none'
};

const night = {
  isNight: true,
  fade: FADE_MS,

  // A shade up off the floor, so the drop into it is a little less far to fall.
  bg: '#16191d',
  fg: '#c9c7c2',
  muted: '#7f7d78',
  rule: '#3a3d44',
  ruleStrong: '#6b6f78',

  panel: '#1b1e23',
  panelBorder: '#6b6f78',
  panelShadow: 'rgba(0, 0, 0, 0.7)',
  tableBorder: '#3a3d44',
  scrim: 'rgba(22, 25, 29, 0.72)',

  logFrom: '#d8d6d1',
  logTo: '#2a2d33',
  logHover: '#ffffff',

  buttonFrom: [27, 30, 35],
  buttonTo: [190, 188, 183],
  buttonDisabled: '#4a4d53',
  buttonDisabledBg: '#1b1e22',
  onLight: '#16191d',
  onDark: '#c9c7c2',
  cooldown: 60,

  // Deeper than day's, and it has to be: black over #16191d has much less to take away than
  // black over #f7f7f5, so matching the two numbers would make the night vignette nearly
  // invisible at exactly the moment it is carrying the warning on its own.
  vignette: 0.82,
  reducedMotion: false,

  mapFilter: 'brightness(0.74) sepia(0.14)'
};

// Theme accessor with a day fallback, so styled components still render outside a ThemeProvider.
const t = key => props => (props.theme && props.theme[key] !== undefined ? props.theme[key] : day[key]);

// Interpolate the button ramp between the palette's two endpoints. Level 0 and max reproduce
// the previous day-mode colours exactly.
const buttonRamp = props => {
  const from = t('buttonFrom')(props);
  const to = t('buttonTo')(props);
  const fraction = Math.min(1, Math.pow((props.level || 0) / 10, 2));
  const rgb = from.map((channel, i) => Math.round(channel + (to[i] - channel) * fraction));

  // Rec. 601 luma, to decide whether the label sits on a light or dark field.
  const luma = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;

  return {
    background: `rgb(${rgb.join(', ')})`,
    text: luma < 140 ? t('onDark')(props) : t('onLight')(props)
  };
};

// The two durations stay module-local: nothing outside needs a number, because every themed
// surface transitions on FADE, and the palettes carry their own.
export { EASE, FADE, RISE_MS, buttonRamp, day, night, t };
